import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import SinConexionKawaii from "../SinConexionKawaii";

// Template28: Página de mapa con iframe de Google Maps (Kioto: Día 1 - Kiyomizudera)
// Contenido desde page.texts, page.images, page.canvasImages y page.maps (servidor).
const Template28 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
	onMapFullscreenChange,
}) => {
	const isSmallScreen = window.innerWidth <= 600;
	const [loaded, setLoaded] = useState(navigator.onLine);
	const [fullscreen, setFullscreen] = useState(false);
	const [isOnline, setIsOnline] = useState(navigator.onLine);

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const headerText = page?.texts?.[0]?.content;
	const rutaText = page?.texts?.[1]?.content;
	const explicacionText = page?.texts?.[2]?.content;
	const mapUrl = page?.maps?.[0];
	const logoImage = getCanvasImageUrl("logo");
	const ninenzakaSticker = getCanvasImageUrl("ninenzaka-sticker");

	const contentColor = textColor || "#444";

	useEffect(() => {
		const handleOnline = () => {
			setIsOnline(true);
			setLoaded(true);
		};
		const handleOffline = () => {
			setIsOnline(false);
			setLoaded(true);
		};

		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);

		return () => {
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
		};
	}, []);

	useEffect(() => {
		const style = document.createElement("style");
		style.innerHTML = `
			@keyframes spin {
				0% { transform: rotate(0deg); }
				100% { transform: rotate(360deg); }
			}
		`;
		document.head.appendChild(style);
		return () => document.head.removeChild(style);
	}, []);

	if (fullscreen && mapUrl) {
		return (
			<Box
				sx={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "#fff",
					zIndex: 9999,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<Box
					sx={{
						padding: "10px",
						textAlign: "right",
						backgroundColor: "#5ba374",
					}}
				>
					<Box
						component="button"
						onClick={() => {
							setFullscreen(false);
							onMapFullscreenChange?.(false);
						}}
						sx={{
							backgroundColor: "#fff",
							color: "#5ba374",
							border: "none",
							borderRadius: "8px",
							padding: "6px 12px",
							fontWeight: 600,
							cursor: "pointer",
							fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
						}}
					>
						← Volver a la guía
					</Box>
				</Box>
				<Box
					component="iframe"
					title="Mapa grande"
					src={mapUrl}
					width="100%"
					height="100%"
					sx={{
						border: 0,
						flex: 1,
					}}
					allowFullScreen
					loading="lazy"
				/>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				overflow: "auto",
				overflowX: "hidden",
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			{/* CABECERA */}
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: headerBackgroundColor || "#5ba374",
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
					fontSize: "14px",
					fontWeight: 800,
					flexShrink: 0,
					zIndex: 2,
					position: "sticky",
					top: 0,
				}}
			>
				{headerText}
				{logoImage && (
					<Box
						component="img"
						src={logoImage}
						alt="logo"
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			{/* MAPA */}
			<Box
				data-block-id="map"
				sx={{
					flex: 1,
					width: "100%",
					minWidth: 0,
					minHeight: 380,
					marginTop: "-70px",
					position: "relative",
					overflow: "hidden",
				}}
			>
				{!loaded && isOnline && (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							height: "100%",
							color: "black",
							fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
							fontSize: "14px",
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							zIndex: 1,
						}}
					>
						<Box
							sx={{
								width: 20,
								height: 20,
								border: "4px solid #ccc",
								borderTop: "4px solid #5ba374",
								borderRadius: "50%",
								animation: "spin 1s linear infinite",
							}}
						/>
					</Box>
				)}

				{isOnline && mapUrl && (
					<Box
						component="button"
						onClick={() => {
							setFullscreen(true);
							onMapFullscreenChange?.(true);
						}}
						sx={{
							position: "absolute",
							top: 90,
							right: 10,
							backgroundColor: "#5ba374",
							color: "#fff!important",
							padding: "8px 10px",
							fontSize: "11px",
							borderRadius: "8px",
							textDecoration: "none",
							fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
							boxShadow: "1px 1px 2px rgba(0,0,0,0.2)",
							border: "none",
							cursor: "pointer",
						}}
					>
						Ver mapa grande
					</Box>
				)}

				{isOnline && mapUrl ? (
					<Box
						component="iframe"
						title="Mapa"
						src={mapUrl}
						onLoad={() => setLoaded(true)}
						sx={{
							position: "absolute",
							top: 0,
							left: 0,
							width: "100%",
							height: "100%",
							minWidth: "100%",
							border: 0,
							display: "block",
						}}
					/>
				) : (
					<SinConexionKawaii height="100%" fontFamily={fontFamily} />
				)}
			</Box>

			{ninenzakaSticker && (
				<Box
					component="img"
					src={ninenzakaSticker}
					alt="NinenzakaSticker"
					sx={{
						width: 120,
						position: "absolute",
						bottom: isSmallScreen ? 120 : 90,
						right: isSmallScreen ? -25 : -25,
					}}
				/>
			)}
			{rutaText && (
				<Box
					sx={{
						position: "absolute",
						bottom: isSmallScreen ? 200 : 150,
						right: isSmallScreen ? 9 : 80,
						backgroundColor: "#fff8db",
						border: "2px solid #f5c400",
						borderRadius: "16px 16px 0px 16px",
						padding: "10px 12px",
						fontSize: "11px",
						fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
						color: contentColor,
						boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
						maxWidth: isSmallScreen ? 150 : 120,
					}}
				>
					{rutaText}
				</Box>
			)}

			{/* EXPLICACIÓN ABAJO */}
			{explicacionText && (
				<Box
					data-block-id="text-1"
					sx={{
						padding: "12px 16px",
						fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
						fontSize: "12px",
						backgroundColor: "#fff",
						color: "#333",
						flexShrink: 0,
					}}
				>
					{explicacionText}
				</Box>
			)}
		</Box>
	);
};

Template28.thumbnail = "/images/template-1.png";

export default Template28;
